import { uniq } from 'lodash-es'
import { action, computed, observable, runInAction } from 'mobx'
import type {
  BulkCreateCustomersFn,
  CreateCustomerFn,
  CreateMemberManuallyFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetSearchableUsersFn,
  GetWorkspaceRolesFn,
  GetWorkspaceSkillsFn,
  GetWorkspaceTeamRolesFn,
  GetWorkspaceTeamsFn,
  ListRolesFn,
  ListSkillsFn,
  ListTeamRolesFn,
  ListTeamsFn,
  SearchMembersFn,
} from '@/features/iam'
import { task } from 'mobx-task'
import { catchError, distinctUntilChanged, EMPTY, switchMap } from 'rxjs'
import { func, pattern, required, validationContext } from 'validx'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import {
  SearchableSelectViewModel,
  type SearchableSelectQuery,
} from '@/infra/SearchableSelectViewModel'
import { type Role } from '@/features/iam/roles/Role'
import { type SearchableUser } from '@/features/iam/users/SearchableUser'
import { type Skill } from '@/features/iam/skills/Skill'
import { type Team } from '@/features/iam/teams/Team'
import { type TeamRole } from '@/features/iam/teams/TeamRole'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { filterNonNil, whileActive, withAbortSignal } from '@/utils/rx-util'
import { Id } from '@/infra/id'
import { CursorPaged } from '@/infra/paging'
import { type Member } from '@/features/iam/members/Member'
import { tapAction, toStream } from '@/utils/mobx-util'
import { pluralize } from '@/utils/pluralization-util'
import { match } from '@/utils/pattern-match-util'
import type { BulkSubmissionResult } from '@/features/iam/customers/bulk-create-util'
import {
  type ParsedCustomerRow,
  parseCsvFile,
} from '@/features/iam/customers/customer-csv'
import { type Portal, portals } from '@/features/iam/members/portal'

interface InviteResults {
  email: string
  success?: boolean
  error?: Error
}

/**
 * Sign-in type selection.
 */
export type SignInType = 'UsernamePassword' | 'AccessLink'

export class WorkspaceInvitationDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly modal = new ModalViewModel()

  /**
   * The list of workspace roles.
   */
  @observable
  accessor workspaceRoles: Role[] = []

  /**
   * The list of available skills.
   */
  @observable
  accessor availableSkills: Skill[] = []

  /**
   * The list of teams.
   */
  @observable
  accessor teams: Team[] = []

  /**
   * The list of team roles.
   */
  @observable
  accessor teamRoles: TeamRole[] = []

  /**
   * The list of portals.
   */
  readonly portals: Portal[] = [...portals]

  /**
   * The current content of the email address text area.
   */
  @observable
  accessor emailAddressTextArea = ''

  /**
   * The currently selected workspace.
   */
  @observable
  accessor workspace: Workspace | null = null

  /**
   * The currently selected workspace role.
   */
  @observable
  accessor workspaceRole: Role | null = null

  /**
   * The currently selected skills.
   */
  @observable
  accessor skills: Skill[] = []

  /**
   * The currently selected team.
   */
  @observable
  accessor team: Team | null = null

  /**
   * The currently selected team role.
   */
  @observable
  accessor teamRole: TeamRole | null = null

  /**
   * The currently selected portal.
   */
  @observable
  accessor portal: Portal = 'CustomerPortal'

  /**
   * The currently selected sign-in type.
   */
  @observable
  accessor signInType: SignInType = 'UsernamePassword'

  /**
   * Customer given name (first name).
   */
  @observable
  accessor customerGivenName = ''

  /**
   * Customer family name (last name).
   */
  @observable
  accessor customerFamilyName = ''

  /**
   * Customer phone number.
   */
  @observable
  accessor customerPhoneNumber = ''

  /**
   * Customer email address.
   */
  @observable
  accessor customerEmail = ''

  /**
   * Whether bulk upload mode is active for customer invitations.
   */
  @observable
  accessor bulkUploadMode = false

  /**
   * Parsed customer data from CSV upload.
   */
  @observable
  accessor bulkCustomers: ParsedCustomerRow[] = []

  /**
   * Error message from CSV parsing (header errors, etc.).
   */
  @observable
  accessor bulkUploadError = ''

  /**
   * Name of the uploaded CSV file.
   */
  @observable
  accessor bulkUploadFileName = ''

  /**
   * Results from the last bulk submission (for showing results view).
   */
  @observable
  accessor bulkSubmissionResults: BulkSubmissionResult[] = []

  /**
   * Whether to show the results view after bulk submission.
   */
  @observable
  accessor showBulkResults = false

  /**
   * Advisor picker. Pages members who can be assigned in the selected workspace.
   */
  readonly advisorSelect = new SearchableSelectViewModel((query, signal) =>
    this.searchAdvisors(query, signal),
  )

  /**
   * Validation rules for regular workspace invitations.
   */
  invitationValidation = validationContext(this, {
    emailAddressTextArea: [
      func(
        () => this.emailAddresses.length > 0,
        'You must enter at least one valid email address.',
      ),
    ],
    workspace: [
      func(() => this.workspace !== null, 'You must select a workspace.'),
    ],
    workspaceRole: [
      func(
        () => this.workspaceRole !== null,
        'You must select a workspace role.',
      ),
    ],
    teamRole: [
      func(
        () => this.team === null || this.teamRole !== null,
        'If you select a team, you must also select a team role.',
      ),
    ],
  })

  /**
   * Validation rules for customer invitations.
   */
  customerValidation = validationContext(this, {
    workspace: [
      func(() => this.workspace !== null, 'You must select a workspace.'),
    ],
    customerEmail: [
      required(),
      pattern('email'),
      func(({ value }: { value: string }) => {
        return value.length <= 320 || "Email can't be more than 320 characters"
      }),
      func(
        () => this.customerEmail.trim().length > 0,
        'You must enter a valid email address',
      ),
    ],
    customerGivenName: [
      func(
        () => this.customerGivenName.trim().length > 0,
        'You must enter a first name',
      ),
    ],
    customerFamilyName: [
      func(
        () => this.customerFamilyName.trim().length > 0,
        'You must enter a last name',
      ),
    ],
    customerPhoneNumber: [
      func(
        (ctx) =>
          // Only run the phone number rule if we have a value,
          // as phone numbers are optional.
          !ctx.value ||
          pattern(
            /^[+]?[ 0-9()-]+$/i,
            'This does not look like a phone number.',
          )(ctx),
      ),
      func(({ value }: { value: string }) => {
        return value.length <= 20 || "Phone can't be more than 20 characters"
      }),
    ],
  })

  /**
   * Stream of changes to the invitation form inputs.
   */
  private readonly _invitationSelections$ = toStream(
    () => this.workspace,
    false,
  ).pipe(
    distinctUntilChanged(),
    filterNonNil(),
    tapAction(() => this.clearSelections()),
    switchMap((workspace) =>
      withAbortSignal((signal) => this._fetchOptions(workspace, signal)).pipe(
        // The retry alert has reported the failure.
        catchError(() => EMPTY),
      ),
    ),
  )

  constructor(
    private readonly _selectedWorkspace: () => Workspace,
    private readonly _selectedUsers: () => Set<Id>,
    private readonly _getSearchableUsers: GetSearchableUsersFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _listRoles: ListRolesFn,
    private readonly _getWorkspaceRoles: GetWorkspaceRolesFn,
    private readonly _listSkills: ListSkillsFn,
    private readonly _getWorkspaceSkills: GetWorkspaceSkillsFn,
    private readonly _listTeams: ListTeamsFn,
    private readonly _getWorkspaceTeams: GetWorkspaceTeamsFn,
    private readonly _listTeamRoles: ListTeamRolesFn,
    private readonly _getWorkspaceTeamRoles: GetWorkspaceTeamRolesFn,
    private readonly _createMemberManually: CreateMemberManuallyFn,
    private readonly _createCustomer: CreateCustomerFn,
    private readonly _bulkCreateCustomers: BulkCreateCustomersFn,
    private readonly _searchMembers: SearchMembersFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this._lifecycle.manage(
      this.advisorSelect.query$.pipe(
        whileActive(
          toStream(() => this.modal.showing && this.canChooseAdvisor, true),
        ),
      ),
    )
    this._lifecycle.manage(this._invitationSelections$)
  }

  /**
   * Activate the view model.
   */
  activate() {
    this._lifecycle.activate()
  }

  /**
   * Deactivate the view model.
   */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * The invitation dialog's description for the selected sign-in type.
   */
  @computed
  get dialogDescription() {
    return this.signInType === 'AccessLink'
      ? 'Fill in the customer information to allow access link-based sign in.'
      : "Fill in the emails of the users you'd like to invite, and complete the required information."
  }

  /**
   * The currently selected workspace.
   */
  @computed
  get selectedWorkspace(): Workspace | null {
    return this._selectedWorkspace() === Workspace.none
      ? null
      : this._selectedWorkspace()
  }

  /**
   * The currently selected users.
   */
  @computed
  get selectedUsers() {
    return this._selectedUsers()
  }

  /**
   * The list of all loaded users.
   */
  @computed
  get storedUsers() {
    return this._getSearchableUsers()
  }

  /**
   * The list of workspaces available to the user.
   */
  @computed
  get workspaces() {
    const availableWorkspaces = this._getCurrentUserWorkspaceMemberships()
      .filter((mb) => mb.member.permissions.adminUserList)
      .map((mb) => mb.workspace)

    return availableWorkspaces
  }

  /**
   * A list of valid email addresses extracted from email address text area.
   */
  @computed
  get emailAddresses() {
    return uniq(
      this.emailAddressTextArea
        .split(/[,|\n]/)
        .map((e) => e.trim().toLowerCase())
        .filter(
          (e) =>
            !!e &&
            validationContext(
              { email: e },
              { email: [pattern('email')] },
            ).validate().isValid,
        ),
    )
  }

  /**
   * Whether the customer invitation option should be shown.
   */
  @computed
  get shouldShowCustomerOption() {
    return (
      this.workspace &&
      this.workspace.config.externalCustomer.enabled &&
      this.workspace.config.externalCustomer.allowRequestingMagicLinks
    )
  }

  /**
   * Whether to use access link sign-in type.
   */
  @computed
  get canUseAccessLink() {
    return this._memberForSelectedWorkspace?.permissions.customerCreate
  }

  /**
   * Whether invited clients can be assigned an advisor in the selected workspace.
   */
  @computed
  get clientAssignmentEnabled() {
    return !!this.workspace?.config.externalCustomer.clientAssignmentEnabled
  }

  /**
   * Whether the current member can choose which advisor to assign.
   */
  @computed
  get canChooseAdvisor() {
    return (
      this.clientAssignmentEnabled &&
      !!this._memberForSelectedWorkspace?.permissions
        .workspaceClientAssignmentManage
    )
  }

  /**
   * The advisor to assign invited clients to, if any.
   */
  @computed
  get assignedAdvisorUserPublicId(): Id {
    if (this.signInType !== 'AccessLink' || !this.canChooseAdvisor) {
      return Id.none
    }
    return this.advisorSelect.selected?.user.publicId || Id.none
  }

  /**
   * Whether to use username/password sign-in type.
   */
  @computed
  get canUseUsernameAndPassword() {
    const permissions = this._memberForSelectedWorkspace?.permissions
    return (
      permissions &&
      permissions.adminRoleList &&
      permissions.adminTeamsView &&
      permissions.adminUserCreate
    )
  }

  @computed
  private get _memberForSelectedWorkspace() {
    if (!this.workspace) {
      return null
    }
    const currentMember = this._getCurrentUserWorkspaceMemberships().find(
      ({ member }) => member.workspace.id === this.workspace?.id,
    )
    return currentMember?.member ?? null
  }

  /**
   * Get the list of valid bulk customers (no validation errors).
   */
  @computed
  get validBulkCustomers() {
    return this.bulkCustomers.filter((c) => c.isValid)
  }

  /**
   * Get successful results from bulk submission.
   */
  @computed
  get successfulBulkResults() {
    return this.bulkSubmissionResults.filter((r) => r.success)
  }

  /**
   * Get failed results from bulk submission.
   */
  @computed
  get failedBulkResults() {
    return this.bulkSubmissionResults.filter((r) => !r.success)
  }

  /**
   * Set the list of workspace roles.
   */
  @action.bound
  setWorkspaceRoles(roles: Role[]) {
    this.workspaceRoles = roles
  }

  /**
   * Set the list of available skills.
   */
  @action.bound
  setAvailableSkills(skills: Skill[]) {
    this.availableSkills = skills
  }

  /**
   * Set the list of teams.
   */
  @action.bound
  setTeams(teams: Team[]) {
    this.teams = teams
  }

  /**
   * Set the list of team roles.
   */
  @action.bound
  setTeamRoles(roles: TeamRole[]) {
    this.teamRoles = roles
  }

  /**
   * Set the email addresses text area.
   */
  @action.bound
  setEmailAddressTextArea(emails: string) {
    this.emailAddressTextArea = emails
  }

  /**
   * Set the workspace field.
   */
  @action.bound
  setWorkspace(workspace?: Workspace | null) {
    this.workspace = workspace ?? null
    if (this.canUseAccessLink && !this.canUseUsernameAndPassword) {
      this.setSignInType('AccessLink')
      return
    }

    this.setSignInType('UsernamePassword')
  }

  /**
   * Set the workspace role field.
   */
  @action.bound
  setWorkspaceRole(role?: Role | null) {
    this.workspaceRole = role ?? null
  }

  /**
   * Set the skills field.
   */
  @action.bound
  setSkills(skills: Skill[]) {
    this.skills = skills
  }

  /**
   * Set the team field.
   */
  @action.bound
  setTeam(team?: Team | null) {
    this.team = team ?? null
  }

  /**
   * Pages members who can be chosen as an advisor in the selected workspace.
   * Uses member search so platform-role permissions are included.
   */
  searchAdvisors = (params: SearchableSelectQuery, signal?: AbortSignal) => {
    const workspaceId = this.workspace?.id
    if (!workspaceId) {
      return Promise.resolve(CursorPaged.empty<Member>())
    }
    return this._searchMembers(
      {
        workspaceId,
        userTypes: ['User'],
        permission: 'clientAssignmentManage',
        searchText: params.searchText,
        limit: params.limit,
        after: params.after,
      },
      signal,
    )
  }

  @action.bound
  setTeamRole(role?: TeamRole | null) {
    this.teamRole = role ?? null
  }

  /**
   * Set the portal field.
   */
  @action.bound
  setPortal(portal?: Portal) {
    if (portal) {
      this.portal = portal
    }
  }

  /**
   * Set the sign-in type.
   */
  @action.bound
  setSignInType(type: SignInType) {
    this.signInType = type
  }

  /**
   * Set the customer given name.
   */
  @action.bound
  setCustomerGivenName(name: string) {
    this.customerGivenName = name
  }

  /**
   * Set the customer family name.
   */
  @action.bound
  setCustomerFamilyName(name: string) {
    this.customerFamilyName = name
  }

  /**
   * Set the customer phone number.
   */
  @action.bound
  setCustomerPhoneNumber(phone: string) {
    this.customerPhoneNumber = phone
  }

  /**
   * Set the customer email.
   */
  @action.bound
  setCustomerEmail(email: string) {
    this.customerEmail = email
  }

  /**
   * Set bulk upload mode.
   */
  @action.bound
  setBulkUploadMode(mode: boolean) {
    this.bulkUploadMode = mode
    if (!mode) {
      this.clearBulkUpload()
    }
  }

  /**
   * Clear bulk upload state.
   */
  @action.bound
  clearBulkUpload() {
    this.bulkCustomers = []
    this.bulkUploadError = ''
    this.bulkUploadFileName = ''
    this.bulkSubmissionResults = []
    this.showBulkResults = false
  }

  /**
   * Dismiss the bulk results view.
   */
  @action.bound
  dismissBulkResults() {
    this.close()
  }

  /**
   * Leave the bulk results view to retry the failed entries.
   */
  @action.bound
  retryFailedBulkResults() {
    if (this.failedBulkResults.length === 0) {
      this.close()
      return
    }

    this.bulkCustomers = this.bulkCustomers.filter((c) =>
      this.failedBulkResults.some((f) => f.email === c.email),
    )
    this.showBulkResults = false
    this.bulkSubmissionResults = []
  }

  /**
   * Handle CSV file upload and parse its contents.
   */
  @action.bound
  async handleCsvUpload(file: File) {
    this.bulkUploadError = ''
    this.bulkUploadFileName = file.name

    try {
      const result = await parseCsvFile(file)

      runInAction(() => {
        if (result.headerError) {
          this.bulkUploadError = result.headerError
          this.bulkCustomers = []
          return
        }

        this.bulkCustomers = result.rows
      })
    } catch {
      runInAction(() => {
        this.bulkUploadError = 'Failed to read CSV file'
        this.bulkCustomers = []
      })
    }
  }

  /**
   * Remove a customer from the bulk upload list.
   */
  @action.bound
  removeBulkCustomer(index: number) {
    this.bulkCustomers.splice(index, 1)
  }

  /**
   * Clears selections when the workspace is changed.
   */
  @action.bound
  clearSelections() {
    this.setWorkspaceRole(null)
    this.setSkills([])
    this.setTeam(null)
    this.setTeamRole(null)
    this.advisorSelect.reset()
  }

  /**
   * Preselects the workspace chosen on the page, if any.
   */
  @action.bound
  private _initialize() {
    if (this.selectedWorkspace !== null) {
      this.setWorkspace(this.selectedWorkspace)
    }
  }

  /**
   * Opens the dialog with the contextual user.
   */
  @action.bound
  showWithUser(user: SearchableUser) {
    this._initialize()
    this.setEmailAddressTextArea(user.email)
    this.modal.show()
  }

  /**
   * Opens the dialog with all the selected users.
   */
  @action.bound
  showWithSelections() {
    this._initialize()
    const selectedUserEmailList = this.storedUsers
      .filter((u) => this.selectedUsers.has(u.publicId))
      .map((u) => u.email)
      .join(', ')
    this.setEmailAddressTextArea(selectedUserEmailList)
    this.modal.show()
  }

  /**
   * Clears workspace invitation dialog state.
   */
  @action.bound
  reset() {
    this.setWorkspaceRoles([])
    this.setAvailableSkills([])
    this.setTeams([])
    this.setTeamRoles([])

    this.setEmailAddressTextArea('')
    this.setWorkspace(null)
    this.setWorkspaceRole(null)
    this.setSkills([])
    this.setTeam(null)
    this.setTeamRole(null)
    this.setPortal('CustomerPortal')
    this.setSignInType('UsernamePassword')
    this.setCustomerGivenName('')
    this.setCustomerFamilyName('')
    this.setCustomerPhoneNumber('')
    this.setCustomerEmail('')
    this.setBulkUploadMode(false)
    this.advisorSelect.reset()

    this.customerValidation.reset()
    this.invitationValidation.reset()
  }

  /**
   * Send regular workspace invitations to the specified users.
   */
  private async _submitInvitations(): Promise<void> {
    const workspace = this.workspace
    if (!this.invitationValidation.reset().validate().isValid || !workspace) {
      return
    }

    const results: InviteResults[] = await Promise.all(
      this.emailAddresses.map((e) =>
        this._createMemberManually({
          email: e,
          workspaceId: workspace.id,
          roleId: this.workspaceRole?.id,
          skillIds: this.skills.map((s) => s.id),
          teamId: this.team?.id,
          teamRoleId: this.teamRole?.id,
          source: 'Local',
          portal: this.portal,
        })
          .then(() => {
            return { email: e, success: true }
          })
          .catch((err: Error) => {
            return { email: e, error: err }
          }),
      ),
    )

    const successful = results.filter((r) => r.success)
    const failed = results.filter((r) => r.error)

    if (failed.length < 1) {
      this._newAlert()
        .withState('success')
        .saying(
          'Success!',
          `Invitations sent to ${successful.length} ${pluralize('user', successful.length)}.`,
        )
        .autoDismiss(2500)
        .show()
      this.close()
      return
    }

    // If any invitations failed, populate email text area with the failed email addresses.
    this._newAlert()
      .withState('error')
      .saying(
        `Failed to send invitations to ${failed.length} ${pluralize('user', failed.length)}.`,
      )
      .autoDismiss(2500)
      .show()
    this.setEmailAddressTextArea(failed.map((f) => f.email).join(', '))
  }

  /**
   * Send customer invitation (single mode).
   */
  private async _submitSingleCustomer(): Promise<void> {
    const workspace = this.workspace
    if (!this.customerValidation.reset().validate().isValid || !workspace) {
      return
    }

    await this._createCustomer({
      workspaceId: workspace.id,
      email: this.customerEmail,
      givenName: this.customerGivenName,
      familyName: this.customerFamilyName,
      phoneNumber: this.customerPhoneNumber || undefined,
      advisorUserPublicId: this.assignedAdvisorUserPublicId,
    }).catch(this._createAlertErrorHandler('Sending the invitation failed'))

    this._newAlert()
      .withState('success')
      .saying('Success!', `Customer invitation sent to ${this.customerEmail}.`)
      .autoDismiss(2500)
      .show()
    this.close()
  }

  /**
   * Send customer invitations (bulk mode).
   * Processes customers in batches of 25 with a delay between batches to avoid rate limiting.
   */
  private async _submitBulkCustomers(): Promise<void> {
    const customers = this.validBulkCustomers
    const workspace = this.workspace
    if (!workspace) {
      return
    }
    if (customers.length === 0) {
      this._newAlert()
        .withState('error')
        .saying('No valid customers to create.')
        .autoDismiss(2500)
        .show()
      return
    }

    const results = await this._bulkCreateCustomers({
      customers,
      workspaceId: workspace.id,
      advisorUserPublicId: this.assignedAdvisorUserPublicId,
    })

    runInAction(() => {
      this.bulkSubmissionResults = results
      this.showBulkResults = true
    })
  }

  /**
   * Send customer invitation (dispatches to single or bulk mode).
   */
  private async _submitCustomer(): Promise<void> {
    if (this.bulkUploadMode) {
      return this._submitBulkCustomers()
    }
    return this._submitSingleCustomer()
  }

  /**
   * Submit the form based on the selected invitation type.
   */
  submit = task.resolved(async () => {
    return match(this.signInType, {
      UsernamePassword: () => this._submitInvitations(),
      AccessLink: () => this._submitCustomer(),
    })
  })

  /**
   * Close the dialog and reset selections to default.
   */
  @action.bound
  close() {
    this.reset()
    this.modal.close()
  }

  private async _fetchOptions(workspace: Workspace, signal: AbortSignal) {
    const workspaceId = workspace.id
    const withRetry = this._createAlertRetryWrapper(
      'Loading workspace options failed',
      signal,
    )
    await withRetry(async () => {
      await this._listRoles({ workspaceId }, signal)
      await this._listSkills({ workspaceId }, signal)
      await this._listTeams({ workspaceId }, signal)
      await this._listTeamRoles({ workspaceId }, signal)
    })
    signal.throwIfAborted()

    runInAction(() => {
      this.workspaceRoles = this._getWorkspaceRoles(workspaceId)
      this.availableSkills = this._getWorkspaceSkills(workspaceId)
      this.teams = this._getWorkspaceTeams(workspaceId)
      this.teamRoles = this._getWorkspaceTeamRoles(workspaceId)
      this.advisorSelect.reset()
    })
  }
}
